/**
 * What an AUDITOR sees in the sidebar (aud-3 · docs/AUDITOR_ENGAGEMENTS.md).
 *
 * An auditor is invited to examine the books, not to run the business, so this
 * is an **allowlist**, not a denylist. A denylist would quietly hand the auditor
 * every new feature the day it shipped, which is the opposite of the promise the
 * client made when they granted the access.
 *
 * Everything left out is left out on purpose. CRM, helpdesk, tasks and the AI
 * tools are how a business is RUN; team management and integrations are
 * settings; and the practice zone is the auditor's own firm, reached from their
 * own org rather than from inside a client's books.
 *
 * Three are kept deliberately, because the work requires them:
 * - `audit-trail`: §143(3) and Rule 11(g) make the edit log itself a matter to
 *   report on, so hiding it would break the report;
 * - `statutory-registers`: the registers under §§85/88/170 are inspected in
 *   every statutory audit;
 * - `period-locks`: finalising a year is the act that closes the audit.
 *
 * The 3CD needs no leaf of its own: it is a chapter of Audit (auditflow,
 * 16 Sep 2026), which is the auditor's first item.
 *
 * **All of Tax & Compliance** is the auditor's (founder, 24 Sep 2026). In
 * practice the CA who audits the books is the one who files the returns,
 * answers the notices and keeps the registers, and hiding the cockpits sent
 * them back to the owner's login for work that is theirs. It began with
 * `itr-filing` alone (18 Sep 2026: the ITR carries the 3CA/3CB acknowledgement
 * and the 3CD's add-backs); the founder then opened the whole group. The leaves
 * are still named one by one, not by group: a leaf added to Tax & Compliance
 * tomorrow fails `sidebarNav.auditor.test.tsx` until it is named here, so
 * handing it over stays a decision, never an accident. The database never hid
 * any of it (aud-11 blinds only CRM, AI chats, notifications, tasks, the
 * network and the wallets); only this list did.
 *
 * This is navigation, which is UX. It is **not** the security boundary: the
 * books chokepoint and RLS decide what an auditor may actually do, and they hold
 * whether or not a link is on screen. Tightening read access row-by-row is
 * aud-11.
 *
 * It lives here rather than beside the other gates in `sidebarNav.tsx` because
 * that file is a `.tsx` whose exports are all lower-case helpers; adding a
 * capitalised constant makes react-refresh read the file as mixing component and
 * non-component exports and fail the lint. Pure data belongs in a `.ts` anyway.
 *
 * @since 1.0.0 (aud-3)
 */
import type { NavZone } from '@/shared/components/Sidebar';

export const AUDITOR_NAV_ITEM_IDS: ReadonlySet<string> = new Set([
    // the auditor's own surfaces: Audit (the engagement, the 3CD, the report
    // and the file are its chapters) and the firm's list
    'audit', 'my-audits', 'audit-trail', 'period-locks',
    // the ledgers themselves
    'accounts', 'journals', 'opening-balances', 'dimensions',
    // who and what the entries are about
    'contacts', 'items',
    // the vouchers behind the numbers
    'sales', 'purchases', 'expenses', 'credit-notes', 'debit-notes',
    'money-in', 'money-out', 'transfers', 'bank-statements', 'bank-reconciliation', 'receivables',
    // balances an auditor has to substantiate
    'inventory', 'fixed-assets', 'loans', 'payroll',
    // the output, and the evidence behind it
    'reports', 'accruals', 'report-builder', 'documents',
    // all of Tax & Compliance (founder, 24 Sep 2026): the CA files the returns,
    // answers the notices and keeps the registers
    'gst-filing', 'itr-filing', 'advance-tax', 'tds-filing', 'payroll-filing',
    'softex', 'gst-tds', 'compliance-reports', 'compliance', 'books-health',
    'notices', 'statutory-registers', 'roc',
]);

/**
 * Narrow the sidebar to the books when the member is an auditor.
 *
 * Composes with the other gates rather than replacing them: a leaf must clear
 * every layer to show, so an auditor still never sees a module the org has not
 * activated. Every other role passes through untouched.
 */
export function applyAuditorGating(zones: NavZone[], role?: string | null): NavZone[] {
    if (role !== 'auditor') return zones;
    return zones
        .map(zone => ({
            ...zone,
            groups: zone.groups
                .map(group => ({
                    ...group,
                    items: group.items.filter(item => AUDITOR_NAV_ITEM_IDS.has(item.id)),
                }))
                .filter(group => group.items.length > 0),
        }))
        .filter(zone => zone.groups.length > 0);
}
